import React, { useState, useEffect } from 'react';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiX,
  FiSearch,
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiUpload,
  FiImage,
  FiSave,
  FiUser,
  FiRefreshCw,
  FiFilter,
  FiCalendar,
  FiArrowUp,
  FiArrowDown,
  FiChevronLeft,
  FiChevronRight,
  FiStar,
  FiHeart,
  FiMenu,
  FiHome,
  FiSettings,
  FiPlay,
  FiType,
  FiAward,
  FiCheck
} from 'react-icons/fi';
import {
  FaUserCircle,
  FaRegEye,
  FaCheckCircle,
  FaRegClock,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaVideo
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TestimonialDashboard = () => {
  // State management
  const [testimonials, setTestimonials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'cr_date', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imageUpload, setImageUpload] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // New states for View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTestimonial, setViewTestimonial] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    ttype: 'text',
    ttitle: '',
    tsub: '',
    tdet: '',
    tvlink: '',
    is_live: 0
  });

  // Check mobile screen
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // API base URL - keep single source of truth
  const API_BASE_URL = 'https://mitrahomebackendapiprocess.pemixcels.com/api/admin/testimonials';

  const fetchTestimonials = async () => {
    setLoading(true);

    try {
      const response = await fetch(API_BASE_URL, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include"
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      if (data.success) {
        // setTestimonials(data.data || []);
        const normalized = (data.data || []).map(t => ({
          ...t, ttype: (t.ttype || '').toString().toLowerCase(),
          is_live: Number(t.is_live),            // converts "1"/"0" to 1/0
          tid: Number(t.tid) || t.tid,
        }));
        setTestimonials(normalized);
        toast.success("Testimonials loaded successfully!");
      } else {
        toast.error("Failed to fetch testimonials");
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toast.error("Failed to fetch testimonials. Using sample data instead.");
      setTestimonials(sampleTestimonials);
    } finally {
      setLoading(false);
    }
  };

  // Sample data for fallback
  const sampleTestimonials = [
    {
      tid: 1,
      ttype: 'video',
      ttitle: 'Amazing Service!',
      tsub: 'CEO, Tech Company',
      tdet: 'The service provided was exceptional. Highly recommended for anyone looking for quality work.',
      timg: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
      tvlink: 'https://www.youtube.com/watch?v=example1',
      cr_date: '2025-10-27T11:18:32.000Z',
      update_time: '2025-10-27T11:20:01.000Z',
      is_live: 1
    },
    {
      tid: 2,
      ttype: 'text',
      ttitle: 'Great Experience',
      tsub: 'Marketing Manager',
      tdet: 'Working with this team has been a wonderful experience. They delivered beyond expectations.',
      timg: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
      tvlink: '',
      cr_date: '2025-10-27T11:20:12.000Z',
      update_time: '2025-10-27T11:20:12.000Z',
      is_live: 1
    },
    {
      tid: 3,
      ttype: 'video',
      ttitle: 'Professional Team',
      tsub: 'Startup Founder',
      tdet: 'The professionalism and dedication of the team is commendable. Will work with them again.',
      timg: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      tvlink: 'https://www.youtube.com/watch?v=example2',
      cr_date: '2025-10-27T11:20:15.000Z',
      update_time: '2025-10-27T11:20:15.000Z',
      is_live: 0
    }
  ];

  // Initialize with API data
  useEffect(() => {
    fetchTestimonials();
  }, [page, searchTerm, filterStatus, filterType]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // setFormData(prev => ({
    //   ...prev,
    //   [name]: type === "checkbox" ? (checked ? 1 : 0) : value
    // }));
    // For checkboxes (is_live) store numeric 1 or 0
    if (type === "checkbox") {
      setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
      return;
    }

    // keep normal behavior for other inputs
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle image upload with preview (client-side only)
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImageUpload(file);
      setImagePreview(previewUrl);
      toast.success('Image uploaded successfully!');
    }
  };

  // Remove uploaded image
  const removeImage = () => {
    setImageUpload(null);
    setImagePreview(null);
    toast.info('Image removed');
  };

  const toggleTestimonialStatus = async (testimonial) => {
    const newStatus = testimonial.is_live === 1 ? 0 : 1;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${testimonial.tid}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ is_live: newStatus }),
      });

      if (!response.ok) throw new Error("Failed to update status");

      // Update state locally
      const updatedTestimonials = testimonials.map((t) =>
        t.tid === testimonial.tid ? { ...t, is_live: newStatus } : t
      );

      setTestimonials(updatedTestimonials);
      toast.success(
        `Status changed to ${newStatus === 1 ? "Live" : "Draft"}`
      );
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  // Create testimonial (send JSON body - backend expects JSON)
  const createTestimonial = async (testimonialData) => {
    try {
      // If user uploaded an image but backend doesn't accept files,
      // we send the imagePreview (data-url) or leave empty.
      const payload = {
        ...testimonialData,
        ttype: (testimonialData.ttype || '').toString().toLowerCase(),
        is_live: Number(testimonialData.is_live || 0),
        timg: imagePreview || testimonialData.timg || '',

      };

      const response = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to create testimonial");

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  const updateTestimonial = async (id, testimonialData) => {
    try {
      const payload = {
        ...testimonialData,
        ttype: (testimonialData.ttype || '').toString().toLowerCase(),
        is_live: Number(testimonialData.is_live || 0),
        timg: imagePreview || testimonialData.timg || '',
      };

      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to update testimonial');
      return await response.json();

    } catch (error) {
      console.error('Error updating testimonial:', error);
      throw error;
    }
  };

  // Delete testimonial - fixed endpoint
  const deleteTestimonialAPI = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        credentials: "include"
      });

      if (!response.ok) throw new Error('Failed to delete testimonial');
      return await response.json();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      throw error;
    }
  };

  // Save testimonial (add or edit)
  const saveTestimonial = async () => {
    setLoading(true);
    try {
      let result;
      if (editingTestimonial) {
        // Update existing testimonial
        result = await updateTestimonial(editingTestimonial.tid, formData);
        const updatedTestimonials = testimonials.map(testimonial =>
          testimonial.tid === editingTestimonial.tid ?
            { ...testimonial, ...formData, timg: imagePreview || testimonial.timg } : testimonial
        );
        setTestimonials(updatedTestimonials);
        toast.success('Testimonial updated successfully!');
      } else {
        // Add new testimonial
        result = await createTestimonial(formData);

        // if backend returned created object inside data.data or data.data
        const created = result.data || result;
        const newTestimonial = {
          tid: created.tid || (Math.floor(Math.random() * 1000000)), // fallback id
          ...formData,
          is_live: Number(formData.is_live || created.is_live || 0),
          timg: imagePreview || formData.timg || '',
          cr_date: created.cr_date || new Date().toISOString(),
          update_time: created.update_time || new Date().toISOString()
        };
        setTestimonials([newTestimonial, ...testimonials]);
        toast.success('Testimonial created successfully!');
      }

      // Reset form and close modal
      resetForm();
      setShowModal(false);
      fetchTestimonials(); // Refresh the list
    } catch (error) {
      console.error("Save testimonial error:", error);
      toast.error('Error saving testimonial. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Edit testimonial
  const editTestimonial = (testimonial) => {
    setFormData({
      ttype: (testimonial.ttype || 'text').toString().toLowerCase(),
      ttitle: testimonial.ttitle || '',
      tsub: testimonial.tsub || '',
      tdet: testimonial.tdet || '',
      tvlink: testimonial.tvlink || '',
      // is_live: testimonial.is_live || 0,
      is_live: Number(testimonial.is_live) ? 1 : 0,
      timg: testimonial.timg || ''
    });
    setEditingTestimonial(testimonial);

    // Set image preview for editing
    setImagePreview(testimonial.timg);

    setShowModal(true);
    toast.info('Editing testimonial: ' + testimonial.ttitle);
  };

  // Delete testimonial
  const deleteTestimonial = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      setLoading(true);
      try {
        await deleteTestimonialAPI(id);
        setTestimonials(testimonials.filter(testimonial => testimonial.tid !== id));
        toast.success('Testimonial deleted successfully!');
        fetchTestimonials(); // Refresh the list
      } catch (error) {
        toast.error('Error deleting testimonial. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      ttype: 'text',
      ttitle: '',
      tsub: '',
      tdet: '',
      tvlink: '',
      is_live: 0
    });
    setImageUpload(null);
    setImagePreview(null);
    setEditingTestimonial(null);
  };

  // Show image in modal
  const showImage = (src) => {
    setSelectedImage(src);
    setShowImageModal(true);
  };

  // View testimonial details modal
  const openViewModal = (testimonial) => {
    setViewTestimonial(testimonial);
    setShowViewModal(true);
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    toast.info(`Sorted by ${key} ${direction === 'asc' ? 'ascending' : 'descending'}`);
  };

  // Filter testimonials based on search and status
  const filteredTestimonials = testimonials.filter(testimonial => {
    const matchesSearch =
      (testimonial.ttitle ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (testimonial.tsub ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (testimonial.tdet ?? "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || testimonial.is_live.toString() === filterStatus;
    const matchesType = filterType === 'all' || (testimonial.ttype || '').toLowerCase() === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Sort testimonials
  const sortedTestimonials = [...filteredTestimonials].sort((a, b) => {
    const aVal = a[sortConfig.key] ?? '';
    const bVal = b[sortConfig.key] ?? '';

    if (aVal < bVal) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aVal > bVal) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Calculate stats
  const totalTestimonials = testimonials.length;
  const liveTestimonials = testimonials.filter(testimonial => testimonial.is_live === 1).length;
  const draftTestimonials = testimonials.filter(testimonial => testimonial.is_live === 0).length;
  const videoTestimonials = testimonials.filter(testimonial => (testimonial.ttype || '').toLowerCase() === 'video').length;

  // Pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Enhanced Responsive Styles (same as blog dashboard)
  const styles = {
    // Color variables
    colors: {
      primary: '#E36324',
      primaryLight: 'rgba(227, 99, 36, 0.1)',
      secondary: '#5DC350',
      secondaryLight: 'rgba(93, 195, 80, 0.1)',
      dark: '#2D3748',
      light: '#f8f9fa',
      gray: '#718096',
      danger: '#E53E3E',
      dangerLight: 'rgba(229, 62, 62, 0.1)',
      warning: '#D69E2E',
      warningLight: 'rgba(214, 158, 46, 0.1)',
      info: '#3182CE',
      infoLight: 'rgba(49, 130, 206, 0.1)',
      border: '#E2E8F0',
      shadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      shadowHover: '0 10px 15px rgba(0, 0, 0, 0.1)',
      gradient: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)'
    },

    // Global styles
    body: {
      backgroundColor: '#F7FAFC',
      color: '#2D3748',
      lineHeight: 1.6,
      margin: 0,
      padding: 0,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      minHeight: '100vh'
    },

    container: {
      maxWidth: 1400,
      margin: '0 auto',
      padding: isMobile ? '0 15px' : '0 20px'
    },

    // Header styles
    header: {
      background: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 100
    },

    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: isMobile ? '15px 0' : '20px 0'
    },

    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: isMobile ? '1.2rem' : '1.5rem',
      fontWeight: 700,
      color: 'white'
    },

    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      color: 'white'
    },

    avatar: {
      width: isMobile ? '35px' : '45px',
      height: isMobile ? '35px' : '45px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.2)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      border: '2px solid rgba(255,255,255,0.3)'
    },

    mobileMenuBtn: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      color: 'white',
      padding: '8px',
      borderRadius: '8px',
      cursor: 'pointer',
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center'
    },

    // Main content
    mainContent: {
      display: 'flex',
      marginTop: isMobile ? '20px' : '30px',
      gap: isMobile ? '15px' : '25px',
      flexDirection: isMobile ? 'column' : 'row'
    },

    // Sidebar
    sidebar: {
      flex: isMobile ? '0 0 auto' : '0 0 280px',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      padding: isMobile ? '15px' : '20px 0',
      height: isMobile ? 'auto' : 'fit-content',
      display: isMobile && !sidebarOpen ? 'none' : 'block',
      position: isMobile ? 'fixed' : 'relative',
      top: isMobile ? '0' : 'auto',
      left: isMobile ? '0' : 'auto',
      width: isMobile ? '280px' : 'auto',
      height: isMobile ? '100vh' : 'auto',
      zIndex: isMobile ? 99 : 'auto',
      overflowY: isMobile ? 'auto' : 'visible'
    },

    sidebarOverlay: {
      display: isMobile && sidebarOpen ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 98
    },

    sidebarMenu: {
      listStyle: 'none',
      margin: 0,
      padding: 0
    },

    sidebarMenuItem: {
      padding: isMobile ? '14px 20px' : '16px 25px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      borderLeft: '4px solid transparent',
      fontSize: isMobile ? '14px' : '15px',
      color: '#718096',
      textDecoration: 'none'
    },

    sidebarMenuItemActive: {
      backgroundColor: 'rgba(227, 99, 36, 0.1)',
      color: '#E36324',
      borderLeft: '4px solid #E36324'
    },

    // Content area
    content: {
      flex: 1,
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      padding: isMobile ? '20px' : '30px',
      border: '1px solid #E2E8F0'
    },

    contentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: isMobile ? '20px' : '30px',
      paddingBottom: isMobile ? '15px' : '25px',
      borderBottom: '2px solid #F7FAFC',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '15px' : '0'
    },

    contentTitle: {
      fontSize: isMobile ? '1.4rem' : '1.75rem',
      fontWeight: 700,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#2D3748'
    },

    headerActions: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },

    // Buttons
    btn: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      borderRadius: '10px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      fontSize: isMobile ? '13px' : '14px',
      fontFamily: 'inherit',
      whiteSpace: 'nowrap'
    },

    btnPrimary: {
      background: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)',
      color: 'white',
      boxShadow: '0 2px 4px rgba(227, 99, 36, 0.3)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(227, 99, 36, 0.4)'
      }
    },

    btnSecondary: {
      backgroundColor: '#5DC350',
      color: 'white',
      boxShadow: '0 2px 4px rgba(93, 195, 80, 0.3)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(93, 195, 80, 0.4)'
      }
    },

    btnDanger: {
      backgroundColor: '#E53E3E',
      color: 'white',
      boxShadow: '0 2px 4px rgba(229, 62, 62, 0.3)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(229, 62, 62, 0.4)'
      }
    },

    btnOutline: {
      backgroundColor: 'transparent',
      color: '#718096',
      border: '2px solid #E2E8F0',
      ':hover': {
        backgroundColor: '#F7FAFC',
        borderColor: '#E36324',
        color: '#E36324'
      }
    },

    btnSm: {
      padding: '6px 12px',
      fontSize: '12px'
    },

    // Stats cards
    statsCards: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: isMobile ? '15px' : '20px',
      marginBottom: isMobile ? '20px' : '30px'
    },

    statCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      padding: isMobile ? '20px' : '25px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      transition: 'transform 0.3s, box-shadow 0.3s',
      border: '1px solid #F0F0F0',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)'
      }
    },

    statValue: {
      fontSize: isMobile ? '1.5rem' : '2rem',
      fontWeight: 700,
      color: '#E36324'
    },

    statLabel: {
      color: '#718096',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: 500
    },

    statIcon: {
      width: isMobile ? '40px' : '50px',
      height: isMobile ? '40px' : '50px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: isMobile ? '1.2rem' : '1.5rem'
    },

    statIconPrimary: {
      backgroundColor: '#E36324'
    },

    statIconSecondary: {
      backgroundColor: '#5DC350'
    },

    statIconInfo: {
      backgroundColor: '#3182CE'
    },

    statIconWarning: {
      backgroundColor: '#D69E2E'
    },

    // Search and filter
    searchFilterContainer: {
      display: 'flex',
      gap: isMobile ? '10px' : '15px',
      marginBottom: isMobile ? '20px' : '25px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row'
    },

    searchInput: {
      flex: isMobile ? '0 0 100%' : 1,
      minWidth: isMobile ? 'auto' : '250px',
      padding: isMobile ? '12px 12px 12px 40px' : '12px 12px 12px 40px',
      border: '1px solid #E2E8F0',
      borderRadius: '10px',
      fontSize: isMobile ? '14px' : '1rem',
      transition: 'border-color 0.3s, box-shadow 0.3s',
      fontFamily: 'inherit'
    },

    filterSelect: {
      padding: isMobile ? '12px' : '12px',
      border: '1px solid #E2E8F0',
      borderRadius: '10px',
      fontSize: isMobile ? '14px' : '1rem',
      backgroundColor: 'white',
      minWidth: isMobile ? '100%' : '150px',
      fontFamily: 'inherit'
    },

    // Table styles
    tableContainer: {
      overflowX: 'auto',
      borderRadius: '12px',
      border: '1px solid #E2E8F0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      WebkitOverflowScrolling: 'touch'
    },

    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '800px' : '900px'
    },

    tableHeader: {
      padding: isMobile ? '12px' : '16px',
      textAlign: 'left',
      borderBottom: '2px solid #E2E8F0',
      backgroundColor: '#F7FAFC',
      fontWeight: 600,
      color: '#2D3748',
      cursor: 'pointer',
      transition: 'all 0.3s',
      fontSize: isMobile ? '13px' : '14px',
      ':hover': {
        backgroundColor: '#EDF2F7'
      }
    },

    tableCell: {
      padding: isMobile ? '12px' : '16px',
      textAlign: 'left',
      borderBottom: '1px solid #E2E8F0',
      fontSize: isMobile ? '13px' : '14px'
    },

    status: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      border: 'none'
    },

    statusActive: {
      backgroundColor: 'rgba(93, 195, 80, 0.15)',
      color: '#276749',
      ':hover': {
        backgroundColor: 'rgba(93, 195, 80, 0.25)'
      }
    },

    statusInactive: {
      backgroundColor: 'rgba(229, 62, 62, 0.15)',
      color: '#C53030',
      ':hover': {
        backgroundColor: 'rgba(229, 62, 62, 0.25)'
      }
    },

    actionButtons: {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap'
    },

    actionBtn: {
      padding: '6px 10px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '11px',
      transition: 'all 0.3s',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: 500,
      whiteSpace: 'nowrap'
    },

    actionEdit: {
      backgroundColor: 'rgba(49, 130, 206, 0.1)',
      color: '#3182CE',
      border: '1px solid rgba(49, 130, 206, 0.2)',
      ':hover': {
        backgroundColor: 'rgba(49, 130, 206, 0.2)',
        transform: 'translateY(-1px)'
      }
    },

    actionDelete: {
      backgroundColor: 'rgba(229, 62, 62, 0.1)',
      color: '#E53E3E',
      border: '1px solid rgba(229, 62, 62, 0.2)',
      ':hover': {
        backgroundColor: 'rgba(229, 62, 62, 0.2)',
        transform: 'translateY(-1px)'
      }
    },

    actionView: {
      backgroundColor: 'rgba(113, 128, 150, 0.1)',
      color: '#718096',
      border: '1px solid rgba(113, 128, 150, 0.2)',
      ':hover': {
        backgroundColor: 'rgba(113, 128, 150, 0.2)',
        transform: 'translateY(-1px)'
      }
    },

    // Modal styles
    modal: {
      display: 'flex',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 1000,
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '10px' : '20px',
      backdropFilter: 'blur(4px)'
    },

    modalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '800px',
      boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
      overflow: 'hidden',
      maxHeight: '90vh',
      overflowY: 'auto'
    },

    modalHeader: {
      padding: isMobile ? '20px' : '25px',
      background: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },

    modalTitle: {
      fontSize: isMobile ? '1.2rem' : '1.4rem',
      fontWeight: 600,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },

    closeBtn: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      color: 'white',
      fontSize: '1.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: isMobile ? '32px' : '36px',
      height: isMobile ? '32px' : '36px',
      borderRadius: '8px',
      transition: 'background-color 0.3s',
      ':hover': {
        backgroundColor: 'rgba(255,255,255,0.3)'
      }
    },

    modalBody: {
      padding: isMobile ? '20px' : '30px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },

    formGroup: {
      marginBottom: isMobile ? '15px' : '20px'
    },

    formLabel: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 600,
      color: '#2D3748',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: isMobile ? '14px' : '15px'
    },

    formControl: {
      width: '100%',
      padding: isMobile ? '10px' : '12px',
      border: '1px solid #E2E8F0',
      borderRadius: '8px',
      fontSize: isMobile ? '14px' : '1rem',
      transition: 'border-color 0.3s, box-shadow 0.3s',
      fontFamily: 'inherit',
      resize: 'vertical'
    },

    textarea: {
      minHeight: '100px',
      resize: 'vertical'
    },

    modalFooter: {
      padding: isMobile ? '20px' : '25px',
      backgroundColor: '#F7FAFC',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
      borderTop: '1px solid #E2E8F0',
      flexWrap: 'wrap'
    },

    // Image preview
    imagePreview: {
      width: isMobile ? '40px' : '50px',
      height: isMobile ? '40px' : '50px',
      objectFit: 'cover',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'transform 0.3s',
      border: '1px solid #E2E8F0'
    },

    imagePreviewLarge: {
      width: '80px',
      height: '80px',
      objectFit: 'cover',
      borderRadius: '8px',
      border: '1px solid #E2E8F0'
    },

    imageModal: {
      display: 'flex',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 1100,
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '10px' : '20px'
    },

    imageModalContent: {
      maxWidth: '90%',
      maxHeight: '90%',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
    },

    imageModalClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      color: 'white',
      fontSize: '2rem',
      cursor: 'pointer',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.3s',
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }
    },

    // Image upload area
    imageUploadArea: {
      border: '2px dashed #E2E8F0',
      borderRadius: '8px',
      padding: isMobile ? '15px' : '20px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'border-color 0.3s',
      marginBottom: '15px',
      ':hover': {
        borderColor: '#E36324'
      }
    },

    imagePreviewContainer: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
      marginTop: '15px'
    },

    imagePreviewItem: {
      position: 'relative',
      display: 'inline-block'
    },

    removeImageBtn: {
      position: 'absolute',
      top: '-8px',
      right: '-8px',
      backgroundColor: '#E53E3E',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      fontSize: '12px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },

    // Loading state
    loadingSpinner: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      flexDirection: 'column',
      gap: '15px'
    },

    // Empty state
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#718096'
    },

    // Pagination
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      marginTop: '20px',
      padding: '20px',
      flexWrap: 'wrap'
    },

    pageButton: {
      padding: '8px 12px',
      border: '1px solid #E2E8F0',
      backgroundColor: 'white',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      fontSize: '14px',
      minWidth: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ':hover': {
        backgroundColor: '#F7FAFC'
      }
    },

    activePage: {
      backgroundColor: '#E36324',
      color: 'white',
      borderColor: '#E36324',
      ':hover': {
        backgroundColor: '#d4561d'
      }
    },

    // Testimonial card for mobile
    testimonialCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #E2E8F0',
      padding: '16px',
      marginBottom: '12px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    },

    testimonialCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px'
    },

    testimonialCardTitle: {
      fontSize: '16px',
      fontWeight: 600,
      margin: '0 0 8px 0',
      color: '#2D3748'
    },

    testimonialCardMeta: {
      display: 'flex',
      gap: '12px',
      fontSize: '12px',
      color: '#718096',
      marginBottom: '12px'
    },

    testimonialCardContent: {
      marginBottom: '12px',
      fontSize: '14px',
      lineHeight: 1.5
    },

    testimonialCardActions: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end'
    },

    // Type badge
    typeBadge: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    },

    typeText: {
      backgroundColor: 'rgba(49, 130, 206, 0.15)',
      color: '#3182CE'
    },

    typeVideo: {
      backgroundColor: 'rgba(229, 62, 62, 0.15)',
      color: '#C53030'
    },

    // Checkbox styles
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    },

    checkbox: {
      width: '18px',
      height: '18px',
      borderRadius: '4px',
      border: '2px solid #E2E8F0',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },

    /* Add these entries into your styles object */
    viewModalCard: {
      width: '880px',
      maxWidth: 'calc(100% - 40px)',
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 30px 90px rgba(2,6,23,0.2)',
      background: '#fff',
    },
    viewModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '14px 20px',
      background: 'linear-gradient(90deg, #E36324 0%, #FF7B42 100%)',
      color: '#fff'
    },
    viewHeaderIcon: {
      width: 40,
      height: 40,
      background: 'rgba(255,255,255,0.12)',
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    viewHeaderAction: {
      background: 'rgba(255,255,255,0.2)',
      color: '#fff',
      border: 'none',
      padding: 8,
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 16
    },

    viewModalBody: {
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 12
    },
    viewTopRow: {
      display: 'flex',
      gap: 18,
      alignItems: 'flex-start'
    },
    avatarWrap: {
      flex: '0 0 auto'
    },
    avatarLarge: {
      width: 84,
      height: 84,
      borderRadius: 12,
      objectFit: 'cover',
      border: '2px solid #f1f5f9',
      background: '#fff'
    },
    titleBlock: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 6
    },
    viewTitle: {
      margin: 0,
      fontSize: 18,
      fontWeight: 700,
      color: '#153243'
    },
    viewSubtitle: {
      color: '#6b7280',
      fontSize: 14
    },
    typeRow: {
      display: 'flex',
      gap: 10,
      marginTop: 8,
      alignItems: 'center'
    },
    typeBadge: {
      padding: '6px 10px',
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6
    },
    statusSmall: {
      padding: '6px 10px',
      borderRadius: 20,
      background: 'rgba(40, 167, 69, 0.08)',
      color: '#2f855a',
      fontWeight: 700,
      fontSize: 12
    },

    viewContent: {
      marginTop: 10,
      paddingTop: 10,
      borderTop: '1px solid #f1f5f9'
    },
    viewParagraph: {
      color: '#2d3748',
      lineHeight: 1.6,
      margin: 0
    },
    viewParagraphMuted: {
      color: '#94a3b8',
      fontStyle: 'italic'
    },

    videoWrap: {
      marginTop: 12,
      borderRadius: 8,
      overflow: 'hidden',
      background: '#000',
      maxWidth: '100%',
      height: 'auto'
    },
    videoIframe: {
      width: '100%',
      height: 360,
      border: 'none',
      display: 'block'
    },
    externalLink: {
      color: '#2563eb',
      textDecoration: 'underline',
      fontWeight: 600
    },

    metaRow: {
      marginTop: 16,
      display: 'flex',
      gap: 36,
      alignItems: 'flex-start'
    },
    metaLabel: {
      color: '#94a3b8',
      fontSize: 12
    },
    metaValue: {
      color: '#334155',
      fontSize: 14,
      fontWeight: 600
    },

    viewModalFooter: {
      padding: '14px 20px',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 12,
      borderTop: '1px solid #f1f5f9',
      background: '#f8fafc'
    }
    ,
    checkboxChecked: {
      backgroundColor: '#E36324',
      borderColor: '#E36324',
      color: 'white'
    }


  };

  // Render testimonial cards for mobile view
  const renderTestimonialCards = () => {
    return sortedTestimonials.map(testimonial => {
      const createdDate = new Date(testimonial.cr_date);
      const formattedDate = createdDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      return (
        <div key={testimonial.tid} style={styles.testimonialCard}>
          <div style={styles.testimonialCardHeader}>
            <div style={{ flex: 1 }}>
              <h3 style={styles.testimonialCardTitle}>{testimonial.ttitle}</h3>
              <div style={styles.testimonialCardMeta}>
                <span>#{testimonial.tid}</span>
                <span style={{
                  ...styles.typeBadge,
                  ...((testimonial.ttype || '').toLowerCase() === 'text' ? styles.typeText : styles.typeVideo)
                }}>
                  {(testimonial.ttype || '').toLowerCase() === 'text' ? <FiType size={10} /> : <FiPlay size={10} />}
                  {testimonial.ttype}
                </span>
                <span>{formattedDate}</span>
              </div>
            </div>
            <button
              style={{
                ...styles.status,
                ...(testimonial.is_live === 1 ? styles.statusActive : styles.statusInactive)
              }}
              onClick={() => toggleTestimonialStatus(testimonial)}
            >
              {testimonial.is_live === 1 ? <FiCheckCircle size={12} /> : <FiClock size={12} />}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <img
              src={testimonial.timg}
              style={{ ...styles.imagePreview, width: '40px', height: '40px' }}
              alt="Testimonial"
              onClick={() => showImage(testimonial.timg)}
            />
            <div style={{ flex: 1 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                  {testimonial.tsub || ""}
                </div>

                <div style={styles.testimonialCardContent}>
                  {(testimonial.tdet ?? "").length > 100
                    ? (testimonial.tdet ?? "").substring(0, 100) + "..."
                    : (testimonial.tdet ?? "")}
                </div>
              </div>
            </div>
          </div>

          {testimonial.tvlink && (
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '12px' }}>
              <FiPlay size={12} style={{ marginRight: '4px' }} />
              Video: {testimonial.tvlink}
            </div>
          )}

          <div style={styles.testimonialCardActions}>
            <button
              style={{ ...styles.actionBtn, ...styles.actionView }}
              onClick={() => openViewModal(testimonial)}
            >
              <FiEye size={12} />
            </button>
            <button
              style={{ ...styles.actionBtn, ...styles.actionEdit }}
              onClick={() => editTestimonial(testimonial)}
            >
              <FiEdit size={12} />
            </button>
            <button
              style={{ ...styles.actionBtn, ...styles.actionDelete }}
              onClick={() => deleteTestimonial(testimonial.tid)}
            >
              <FiTrash2 size={12} />
            </button>
          </div>
        </div>
      );
    });
  };

  // NOTE: In the full file below the styles object should be the full one you already had.
  // To avoid truncation in this message, ensure you copy your original styles contents into the styles variable above.

  return (
    <div style={styles.body}>
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Sidebar Overlay for Mobile */}
      {isMobile && (
        <div
          style={styles.sidebarOverlay}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div style={styles.container}>
        <div style={styles.mainContent}>
          {/* Content Area */}
          <main style={styles.content}>
            <div style={styles.contentHeader}>
              <h2 style={styles.contentTitle}>
                <FiAward />
                Testimonial Management
              </h2>
              <div style={styles.headerActions}>
                <button
                  style={{ ...styles.btn, ...styles.btnOutline }}
                  onClick={fetchTestimonials}
                  disabled={loading}
                >
                  <FiRefreshCw size={16} />
                  {!isMobile && 'Refresh'}
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnPrimary }}
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  <FiPlus size={16} />
                  {!isMobile && 'Add New Testimonial'}
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div style={styles.statsCards}>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconPrimary }}>
                  <FiAward size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{totalTestimonials}</div>
                <div style={styles.statLabel}>Total Testimonials</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconSecondary }}>
                  <FaCheckCircle size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{liveTestimonials}</div>
                <div style={styles.statLabel}>Live Testimonials</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconInfo }}>
                  <FaVideo size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{videoTestimonials}</div>
                <div style={styles.statLabel}>Video Testimonials</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconWarning }}>
                  <FaRegClock size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{draftTestimonials}</div>
                <div style={styles.statLabel}>Draft Testimonials</div>
              </div>
            </div>

            {/* Search and Filter */}
            <div style={styles.searchFilterContainer}>
              <div style={{ position: 'relative', flex: isMobile ? '0 0 100%' : 1, minWidth: isMobile ? 'auto' : '250px' }}>
                <FiSearch
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#718096'
                  }}
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search testimonials..."
                  style={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#E36324';
                    e.target.style.boxShadow = '0 0 0 2px rgba(227, 99, 36, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <select
                style={styles.filterSelect}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="1">Live</option>
                <option value="0">Draft</option>
              </select>
              <select
                style={styles.filterSelect}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="text">Text</option>
                <option value="video">Video</option>
              </select>
            </div>

            {/* Data Table (Desktop) / Cards (Mobile) */}
            {isMobile ? (
              // Mobile Cards View
              <div>
                {loading ? (
                  <div style={styles.loadingSpinner}>
                    <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <div>Loading testimonials...</div>
                  </div>
                ) : sortedTestimonials.length === 0 ? (
                  <div style={styles.emptyState}>
                    <FiAward size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
                    <h3>No testimonials found</h3>
                    <p>Try adjusting your search or filter criteria</p>
                  </div>
                ) : (
                  renderTestimonialCards()
                )}
              </div>
            ) : (
              // Desktop Table View
              <div style={styles.tableContainer}>
                {loading ? (
                  <div style={styles.loadingSpinner}>
                    <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <div>Loading testimonials...</div>
                  </div>
                ) : sortedTestimonials.length === 0 ? (
                  <div style={styles.emptyState}>
                    <FiAward size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
                    <h3>No testimonials found</h3>
                    <p>Try adjusting your search or filter criteria</p>
                  </div>
                ) : (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('tid')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            ID
                            {sortConfig.key === 'tid' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('ttitle')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Title
                            {sortConfig.key === 'ttitle' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th style={styles.tableHeader}>Subtitle</th>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('ttype')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Type
                            {sortConfig.key === 'ttype' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th style={styles.tableHeader}>Image</th>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('is_live')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Status
                            {sortConfig.key === 'is_live' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('cr_date')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Created At
                            {sortConfig.key === 'cr_date' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th style={styles.tableHeader}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTestimonials.map(testimonial => {
                        const createdDate = new Date(testimonial.cr_date);
                        const formattedDate = createdDate.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });

                        return (
                          <tr key={testimonial.tid}>
                            <td style={styles.tableCell}>
                              <strong>#{testimonial.tid}</strong>
                            </td>
                            <td style={styles.tableCell}>
                              <div>
                                <strong style={{ display: 'block', marginBottom: '5px' }}>{testimonial.ttitle}</strong>
                                {testimonial.tdet && (
                                  <small style={{ color: '#718096', fontSize: '12px' }}>
                                    {testimonial.tdet.substring(0, 60)}...
                                  </small>
                                )}
                              </div>
                            </td>
                            <td style={styles.tableCell}>
                              <span style={{ color: '#718096', fontSize: '0.9rem' }}>
                                {testimonial.tsub}
                              </span>
                            </td>
                            <td style={styles.tableCell}>
                              <span style={{
                                ...styles.typeBadge,
                                ...(testimonial.ttype === 'text' ? styles.typeText : styles.typeVideo)
                              }}>
                                {testimonial.ttype === 'text' ? <FiType size={12} /> : <FiPlay size={12} />}
                                {testimonial.ttype}
                              </span>
                            </td>
                            <td style={styles.tableCell}>
                              <img
                                src={testimonial.timg}
                                style={styles.imagePreview}
                                alt="Testimonial"
                                onClick={() => showImage(testimonial.timg)}
                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                              />
                            </td>
                            <td style={styles.tableCell}>
                              <button
                                style={{
                                  ...styles.status,
                                  ...(testimonial.is_live === 1 ? styles.statusActive : styles.statusInactive)
                                }}
                                onClick={() => toggleTestimonialStatus(testimonial)}
                              >
                                {testimonial.is_live === 1 ? <FiCheckCircle size={12} /> : <FiClock size={12} />}
                                {testimonial.is_live === 1 ? 'Live' : 'Draft'}
                              </button>
                            </td>
                            <td style={styles.tableCell}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <FiCalendar size={12} style={{ color: '#718096' }} />
                                {formattedDate}
                              </div>
                            </td>
                            <td style={styles.tableCell}>
                              <div style={styles.actionButtons}>
                                <button
                                  style={{ ...styles.actionBtn, ...styles.actionView }}
                                  onClick={() => openViewModal(testimonial)}
                                >
                                  <FiEye size={14} />
                                  View
                                </button>
                                <button
                                  style={{ ...styles.actionBtn, ...styles.actionEdit }}
                                  onClick={() => editTestimonial(testimonial)}
                                >
                                  <FiEdit size={14} />
                                  Edit
                                </button>
                                <button
                                  style={{ ...styles.actionBtn, ...styles.actionDelete }}
                                  onClick={() => deleteTestimonial(testimonial.tid)}
                                >
                                  <FiTrash2 size={14} />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.pageButton}
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FiChevronLeft />
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    style={{
                      ...styles.pageButton,
                      ...(page === index + 1 ? styles.activePage : {})
                    }}
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  style={styles.pageButton}
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Add/Edit Testimonial Modal */}
      {showModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                <FiAward />
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h3>
              <button
                style={styles.closeBtn}
                onClick={() => setShowModal(false)}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
              >
                <FiX />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel} htmlFor="ttitle">
                  <FiEdit />
                  Title
                </label>
                <input
                  type="text"
                  style={styles.formControl}
                  id="ttitle"
                  name="ttitle"
                  value={formData.ttitle}
                  onChange={handleInputChange}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#E36324';
                    e.target.style.boxShadow = '0 0 0 2px rgba(227, 99, 36, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.boxShadow = 'none';
                  }}
                  required
                  placeholder="Enter testimonial title"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel} htmlFor="tsub">
                  <FiUser />
                  Subtitle (Position/Company)
                </label>
                <input
                  type="text"
                  style={styles.formControl}
                  id="tsub"
                  name="tsub"
                  value={formData.tsub}
                  onChange={handleInputChange}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#E36324';
                    e.target.style.boxShadow = '0 0 0 2px rgba(227, 99, 36, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.boxShadow = 'none';
                  }}
                  required
                  placeholder="Enter subtitle (e.g., CEO, Tech Company)"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel} htmlFor="ttype">
                  <FiType />
                  Type
                </label>
                <select
                  style={styles.formControl}
                  id="ttype"
                  name="ttype"
                  value={formData.ttype}
                  onChange={handleInputChange}
                  required
                >
                  <option value="text">Text</option>
                  <option value="video">Video</option>
                </select>
              </div>

              {formData.ttype === 'video' && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel} htmlFor="tvlink">
                    <FiPlay />
                    Video Link
                  </label>
                  <input
                    type="url"
                    style={styles.formControl}
                    id="tvlink"
                    name="tvlink"
                    value={formData.tvlink}
                    onChange={handleInputChange}
                    placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                  />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.formLabel} htmlFor="tdet">
                  <FiFileText />
                  Testimonial Content
                </label>
                <textarea
                  style={{ ...styles.formControl, ...styles.textarea }}
                  id="tdet"
                  name="tdet"
                  value={formData.tdet}
                  onChange={handleInputChange}
                  placeholder="Enter testimonial content"
                  rows="4"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  <FiImage />
                  Image
                </label>

                {/* Image Upload Area */}
                <div style={styles.imageUploadArea}>
                  <label style={{ cursor: 'pointer', display: 'block' }}>
                    <FiUpload style={{ marginRight: '5px' }} />
                    Upload Testimonial Image
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                      accept="image/*"
                    />
                  </label>
                  {imagePreview && (
                    <div style={styles.imagePreviewContainer}>
                      <div style={styles.imagePreviewItem}>
                        <img
                          src={imagePreview}
                          style={styles.imagePreviewLarge}
                          alt="Testimonial preview"
                        />
                        <button
                          style={styles.removeImageBtn}
                          onClick={removeImage}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>


              <div style={styles.formGroup}>
                <label style={styles.checkboxContainer}>
                  <div style={{
                    ...styles.checkbox,
                    ...(formData.is_live === 1 ? styles.checkboxChecked : {})
                  }}>
                    {formData.is_live === 1 && <FiCheck size={12} />}
                  </div>
                  <input
                    type="checkbox"
                    name="is_live"
                    checked={formData.is_live === 1}
                    onChange={handleInputChange}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontWeight: 600 }}>Publish Testimonial</span>
                </label>
                <small style={{ color: '#718096', marginLeft: '26px' }}>
                  When checked, the testimonial will be visible on the website
                </small>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.btn, ...styles.btnOutline }}
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                <FiX style={{ marginRight: '5px' }} />
                Cancel
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={saveTestimonial}
                disabled={loading}
              >
                {loading ? (
                  <>Loading...</>
                ) : (
                  <>
                    <FiSave style={{ marginRight: '5px' }} />
                    {editingTestimonial ? 'Update Testimonial' : 'Save Testimonial'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && (
        <div style={styles.imageModal}>
          <span
            style={styles.imageModalClose}
            onClick={() => setShowImageModal(false)}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.5)'}
          >
            <FiX />
          </span>
          <img
            style={styles.imageModalContent}
            src={selectedImage}
            alt="Preview"
          />
        </div>
      )}

      {/* View Testimonial Modal */}
      {/* View Testimonial Modal — REPLACE your existing block with this */}
      {showViewModal && viewTestimonial && (
        <div style={styles.modal}>
          <div style={styles.viewModalCard}>
            <div style={styles.viewModalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={styles.viewHeaderIcon}><FiEye size={20} /></div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>View Testimonial</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  style={styles.viewHeaderAction}
                  onClick={() => {
                    setShowViewModal(false);
                  }}
                  aria-label="close"
                >
                  <FiX />
                </button>
              </div>
            </div>

            <div style={styles.viewModalBody}>
              <div style={styles.viewTopRow}>
                <div style={styles.avatarWrap}>
                  <img
                    src={viewTestimonial.timg || '/placeholder-avatar.png'}
                    alt={viewTestimonial.ttitle}
                    style={styles.avatarLarge}
                  />
                </div>

                <div style={styles.titleBlock}>
                  <h2 style={styles.viewTitle}>{viewTestimonial.ttitle}</h2>
                  <div style={styles.viewSubtitle}>{viewTestimonial.tsub}</div>
                  <div style={styles.typeRow}>
                    <span style={{
                      ...styles.typeBadge,
                      ...((viewTestimonial.ttype || '').toLowerCase() === 'text' ? styles.typeText : styles.typeVideo)
                    }}>
                      {(viewTestimonial.ttype || '').toLowerCase() === 'text' ? 'Text' : 'Video'}
                    </span>

                    {/* <span style={styles.statusSmall}>
                      {viewTestimonial.is_live === 1 ? 'Live' : 'Draft'}
                    </span> */}
                  </div>
                </div>
              </div>

              <div style={styles.viewContent}>
                {viewTestimonial.tdet ? (
                  <p style={styles.viewParagraph}>{viewTestimonial.tdet}</p>
                ) : (
                  <p style={styles.viewParagraphMuted}>No text content provided.</p>
                )}

                {/* Video embed or link */}
                {viewTestimonial.tvlink ? (
                  // If tvlink already contains '/embed/' use it directly, otherwise try to convert common youtube watch URL
                  (function renderVideo() {
                    const url = String(viewTestimonial.tvlink || '');
                    let embed = url;
                    if (!url.includes('/embed/') && url.includes('youtube.com/watch')) {
                      try {
                        const vid = new URL(url).searchParams.get('v');
                        if (vid) embed = `https://www.youtube.com/embed/${vid}`;
                      } catch (e) {
                        embed = url;
                      }
                    }
                    // Only show iframe for youtube/embed links, otherwise show clickable link
                    if (embed.includes('youtube.com/embed')) {
                      return (
                        <div style={styles.videoWrap}>
                          <iframe
                            title={`video-${viewTestimonial.tid}`}
                            src={embed}
                            style={styles.videoIframe}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div style={{ marginTop: 14 }}>
                          <a href={url} target="_blank" rel="noreferrer" style={styles.externalLink}>
                            {url}
                          </a>
                        </div>
                      );
                    }
                  })()
                ) : null}

                <div style={styles.metaRow}>
                  <div>
                    <small style={styles.metaLabel}>Created at</small>
                    <div style={styles.metaValue}>
                      {viewTestimonial.cr_date ? new Date(viewTestimonial.cr_date).toLocaleString() : '-'}
                    </div>
                  </div>

                  <div>
                    <small style={styles.metaLabel}>Status</small>
                    <div style={styles.metaValue}>
                      {viewTestimonial.is_live === 1 ? 'Live' : 'Draft'}
                    </div>
                  </div>

                  <div>
                    <small style={styles.metaLabel}>Type</small>
                    <div style={styles.metaValue}>
                      {(viewTestimonial.ttype || '').toString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.viewModalFooter}>
              <button
                style={{ ...styles.btn, ...styles.btnOutline }}
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>

              {/* <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={() => {
                  // Open edit modal prefilled
                  editTestimonial(viewTestimonial);
                  setShowViewModal(false);
                  setShowModal(true);
                }}
              >
                Edit
              </button> */}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TestimonialDashboard;
